// Recent accounts (owner 2026-10-25): the accounts this browser has signed in
// with, for the sign-in page's "Choose an account". Remembered by default;
// "This is a shared device" opts out (sign-in-memory.js).
//
// This browser only: localStorage, every access guarded, so a private
// window or blocked storage gives an empty list and never an error.
//
// Kept per account (version 1):
//   email       the address the session's token names
//   name        first + last from the token, when it has them
//   avatarURL   an https picture, when one is known (the admin has none
//               today: the chooser draws initials on a colour from the email)
//   method      how they signed in last: { kind: 'password' | 'sso' |
//               'device' }, and for single sign-on the connection's
//               { identifier, displayName, iconKey? }; iconKey is a small
//               brand key (microsoft, google, okta, ...) the chooser draws
//               from its own library -- never an SVG. Optional: entries
//               without it (made before it existed) load as before. A hint only: the server still
//               decides how an email signs in.
//   lastUsedAt  ISO time of that sign-in
//
// Never kept: passwords, tokens, codes, organization data. An entry is built
// field by field (cleanAccount), so nothing else a caller hands over is
// stored. At most MAX_RECENT_ACCOUNTS, most recent first.
//
// The key carries the version, and so does the stored value: a value of a
// version this build doesn't know is left alone (read as empty, never
// overwritten), so an older tab can't destroy a newer build's list. A value
// that isn't ours at all (not JSON, no version) is junk and is replaced.
//
// The order is the order of sign-ins (each one goes to the top), never
// re-sorted by lastUsedAt: a clock set back must not push a new sign-in off
// the list.

export const RECENT_ACCOUNTS_KEY = 'edxr-recent-accounts-v1'
export const RECENT_ACCOUNTS_VERSION = 1
export const MAX_RECENT_ACCOUNTS = 5

export const METHOD_KINDS = ['password', 'sso', 'device']

const MAX_EMAIL = 320
const MAX_TEXT = 200
const MAX_URL = 2048
const ICON_KEY = /^[a-z0-9-]{1,32}$/

const defaultStorage = () => {
  try {
    return window.localStorage
  } catch (e) {
    return null
  }
}

const text = (value, max) => {
  if (typeof value !== 'string') return ''
  const v = value.trim()
  return v.length <= max ? v : ''
}

export const emailKey = email => text(email, MAX_EMAIL).toLowerCase()

// Only an https picture: never a data: or javascript: URL from storage.
const httpsURL = value => {
  const v = text(value, MAX_URL)
  if (!v) return ''
  try {
    return new URL(v).protocol === 'https:' ? v : ''
  } catch (e) {
    return ''
  }
}

const isoTime = value => {
  if (typeof value !== 'string' && typeof value !== 'number') return null
  const t = new Date(value)
  return isNaN(t.getTime()) ? null : t.toISOString()
}

// The method, field by field. null for anything this build doesn't know.
export const cleanMethod = method => {
  if (!method || typeof method !== 'object') return null
  if (!METHOD_KINDS.includes(method.kind)) return null

  const clean = { kind: method.kind }

  if (method.kind === 'sso' && method.connection && typeof method.connection === 'object') {
    const identifier = text(method.connection.identifier, MAX_TEXT)
    const displayName = text(method.connection.displayName, MAX_TEXT)
    const iconKey = ICON_KEY.test(method.connection.iconKey) ? method.connection.iconKey : ''
    if (identifier || displayName) {
      clean.connection = {}
      if (identifier) clean.connection.identifier = identifier
      if (displayName) clean.connection.displayName = displayName
      if (iconKey) clean.connection.iconKey = iconKey
    }
  }

  return clean
}

// One account, field by field; null when it has no usable email.
export const cleanAccount = account => {
  if (!account || typeof account !== 'object') return null

  const email = text(account.email, MAX_EMAIL)
  if (!email) return null

  const clean = { email }

  const name = text(account.name, MAX_TEXT)
  if (name) clean.name = name

  const avatarURL = httpsURL(account.avatarURL)
  if (avatarURL) clean.avatarURL = avatarURL

  const method = cleanMethod(account.method)
  if (method) clean.method = method

  clean.lastUsedAt = isoTime(account.lastUsedAt)

  return clean
}

// In the order given (most recent first), one entry per email (the first),
// at most the cap.
const tidy = accounts => {
  const seen = new Set()
  const out = []
  accounts.forEach(account => {
    const key = emailKey(account.email)
    if (seen.has(key)) return
    seen.add(key)
    out.push(account)
  })
  return out.slice(0, MAX_RECENT_ACCOUNTS)
}

// { accounts, writable }: writable is false when storage is blocked or holds
// a version this build doesn't know.
const load = storage => {
  if (!storage) return { accounts: [], writable: false }

  let raw
  try {
    raw = storage.getItem(RECENT_ACCOUNTS_KEY)
  } catch (e) {
    return { accounts: [], writable: false }
  }

  if (!raw) return { accounts: [], writable: true }

  let value
  try {
    value = JSON.parse(raw)
  } catch (e) {
    return { accounts: [], writable: true }
  }

  if (!value || typeof value !== 'object' || Array.isArray(value) || typeof value.version !== 'number') return { accounts: [], writable: true }
  if (value.version !== RECENT_ACCOUNTS_VERSION) return { accounts: [], writable: false }

  const accounts = Array.isArray(value.accounts) ? value.accounts.map(cleanAccount).filter(Boolean) : []
  return { accounts: tidy(accounts), writable: true }
}

const save = (storage, accounts) => {
  try {
    if (accounts.length) {
      storage.setItem(RECENT_ACCOUNTS_KEY, JSON.stringify({ version: RECENT_ACCOUNTS_VERSION, accounts }))
    } else {
      storage.removeItem(RECENT_ACCOUNTS_KEY)
    }
    return true
  } catch (e) {
    return false
  }
}

// The remembered accounts, most recent first.
export const readRecentAccounts = ({ storage = defaultStorage() } = {}) => load(storage).accounts

export const isRemembered = (email, { storage = defaultStorage() } = {}) => {
  const key = emailKey(email)
  return !!key && load(storage).accounts.some(account => emailKey(account.email) === key)
}

// Adds or refreshes an account at the top. Answers the list as it now is.
export const rememberAccount = (account, { storage = defaultStorage(), now = new Date() } = {}) => {
  const { accounts, writable } = load(storage)
  const clean = cleanAccount({ ...account, lastUsedAt: isoTime(now && now.getTime ? now.getTime() : now) })
  if (!clean || !writable) return accounts

  const key = emailKey(clean.email)
  const next = tidy([clean, ...accounts.filter(a => emailKey(a.email) !== key)])
  return save(storage, next) ? next : accounts
}

// Removes one account. Answers the list without it (what the page shows,
// even when storage refused the write).
export const forgetAccount = (email, { storage = defaultStorage() } = {}) => {
  const key = emailKey(email)
  const { accounts, writable } = load(storage)
  const next = accounts.filter(a => emailKey(a.email) !== key)
  if (key && writable && next.length !== accounts.length) save(storage, next)
  return next
}

// "Forget all accounts on this device": the key goes, whatever version it
// held.
export const forgetAllAccounts = ({ storage = defaultStorage() } = {}) => {
  try {
    if (storage) storage.removeItem(RECENT_ACCOUNTS_KEY)
  } catch (e) {}
  return []
}
